import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { safeDestroy } from "../../hooks/useFlickity";

export interface ICardSlider {
    imageUrl: string;
    copy: string;
}

// Not using useFlickity hook: requires resize-based conditional init/destroy

const initFlickity = (
    FlickityClass: typeof Flickity,
    carousel: HTMLUListElement,
): Flickity => {
    return new FlickityClass(carousel, {
        cellAlign: "left",
        wrapAround: true,
        pageDots: true,
        imagesLoaded: true,
        arrowShape: {
            x0: 15,
            x1: 100,
            y1: 110,
            x2: 105,
            y2: 110,
            x3: 20,
        },
    });
};

const shouldUseCarousel = (
    carousel: HTMLUListElement,
    isWindowMobile: boolean,
): boolean => {
    const carouselLength = carousel.querySelectorAll(
        ".card-slider__column",
    ).length;
    return carouselLength >= 4 || (carouselLength >= 2 && isWindowMobile);
};

const buildFullImages = (
    images: readonly ICardSlider[],
): React.JSX.Element[] => {
    return images.map((img, i) => {
        const fullStyle: React.CSSProperties = {
            backgroundImage: `url(${img.imageUrl})`,
        };
        return (
            <li key={i} className="card-slider__column">
                <div className="card-slider__image" style={fullStyle}></div>
                <div className="card-slider__copy-container">
                    <div
                        className="card-slider__copy"
                        dangerouslySetInnerHTML={{ __html: img.copy }}
                    ></div>
                </div>
            </li>
        );
    });
};

export const CardSlider = (
    props: Immutable<{
        images: ICardSlider[];
    }>,
): React.ReactNode => {
    const [isNormalLayout, setIsNormalLayout] = React.useState(false);
    const carouselRef = React.useRef<HTMLUListElement>(null);
    const flickityRef = React.useRef<Flickity | null>(null);

    React.useEffect(() => {
        const window = getWindow();
        const carouselElem = carouselRef.current;
        if (!carouselElem || !window) {
            return;
        }

        let resizeHandler: (() => void) | null = null;

        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        import("flickity").then((module) => {
            const FlickityClass = module.default;

            const recalcFlickityDisplay = (): void => {
                if (!window) return;
                const isWindowMobile = window.innerWidth < 501;
                const useCarousel = shouldUseCarousel(
                    carouselElem,
                    isWindowMobile,
                );

                if (useCarousel) {
                    setIsNormalLayout(false);
                    if (flickityRef.current) {
                        safeDestroy(flickityRef.current);
                    }
                    flickityRef.current = initFlickity(
                        FlickityClass,
                        carouselElem,
                    );
                } else {
                    if (flickityRef.current) {
                        safeDestroy(flickityRef.current);
                        flickityRef.current = null;
                    }
                    setIsNormalLayout(true);
                }
            };

            recalcFlickityDisplay();
            resizeHandler = recalcFlickityDisplay;
            window.addEventListener("resize", recalcFlickityDisplay);
        });

        return () => {
            if (resizeHandler) {
                window.removeEventListener("resize", resizeHandler);
            }
            if (flickityRef.current) {
                safeDestroy(flickityRef.current);
                flickityRef.current = null;
            }
        };
    }, []);

    // Flickity adds its own classes to the list and its cells, so layout
    // state lives in an attribute and the classNames never change.
    return (
        <div className="card-slider__carousel-content">
            <ul
                className="card-slider__columns"
                data-layout={isNormalLayout ? "normal" : "carousel"}
                ref={carouselRef}
            >
                {buildFullImages(props.images)}
            </ul>
        </div>
    );
};
